import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import API from "@/lib/api";
import { ArrowLeft, Check, Clock, Truck, Package as PackageIcon, Palette } from "lucide-react";

const STATUS_STEPS = [
  { key: "pending", label: "Pendiente", icon: Clock },
  { key: "design", label: "Diseño", icon: Palette },
  { key: "production", label: "Producción", icon: PackageIcon },
  { key: "ready", label: "Listo", icon: Check },
  { key: "delivered", label: "Entregado", icon: Truck },
];

const STATUS_LABELS = { pending: "Pendiente", design: "Diseño", production: "En producción", ready: "Listo", delivered: "Entregado" };

function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    API.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
  }, [orderId]);

  if (!order) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><p className="text-sm text-[#94A3B8]">Cargando...</p></div>;

  const currentIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="order-detail-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/portal/pedidos" className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-6" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> Volver a Pedidos
        </Link>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] mb-6" style={{ fontFamily: 'Chivo' }}>Seguimiento de Pedido</h1>

        {/* Timeline */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 mb-6" data-testid="order-timeline">
          <div className="flex items-center justify-between mb-6">
            {STATUS_STEPS.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "bg-[#0047AB] border-[#0047AB] text-white" : "border-[#E2E8F0] text-[#94A3B8]"} ${active ? "ring-4 ring-[#0047AB]/20" : ""}`}>
                    <s.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] mt-2 font-medium ${done ? "text-[#0047AB]" : "text-[#94A3B8]"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
          <div className="relative h-1 bg-[#E2E8F0] rounded-full">
            <div className="absolute h-1 bg-[#0047AB] rounded-full transition-all" style={{ width: `${(currentIdx / (STATUS_STEPS.length - 1)) * 100}%` }} />
          </div>
        </div>

        {/* Order info */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
          <h3 className="text-sm font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Chivo' }}>Detalle del Pedido</h3>
          <div className="space-y-3">
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-[#475569]">{item.product_name} ({item.technique_name})</span>
                <span className="font-medium text-[#0F172A]">{item.quantity} pzas · Q{item.subtotal}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 border-t border-[#E2E8F0] text-base font-bold text-[#0F172A]">
              <span>Total</span>
              <span data-testid="order-total">Q{order.total}</span>
            </div>
            {order.delivery_date && (
              <div className="flex justify-between text-sm"><span className="text-[#475569]">Entrega programada:</span><span className="font-medium">{order.delivery_date}</span></div>
            )}
          </div>

          {/* Status history */}
          {order.status_history && order.status_history.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-3">Historial</h4>
              <div className="space-y-2">
                {order.status_history.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="w-2 h-2 rounded-full bg-[#0047AB]" />
                    <span className="text-[#475569]">{new Date(h.timestamp).toLocaleString()}</span>
                    <span className="font-medium text-[#0F172A]">{STATUS_LABELS[h.status] || h.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientOrders() {
  const { orderId } = useParams();
  if (orderId) return <OrderDetail />;
  return <OrdersList />;
}

function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/orders").then((r) => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="client-orders-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Mi Portal</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] mb-8" style={{ fontFamily: 'Chivo' }}>Mis Pedidos</h1>
        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          {loading ? (
            <p className="px-5 py-12 text-sm text-[#94A3B8] text-center">Cargando...</p>
          ) : orders.length === 0 ? (
            <p className="px-5 py-12 text-sm text-[#94A3B8] text-center">No tienes pedidos activos</p>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {orders.map((o) => (
                <Link key={o.id} to={`/portal/pedidos/${o.id}`} className="px-5 py-4 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors block" data-testid={`order-row-${o.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{o.items?.map((i) => i.product_name).join(", ")}</p>
                    <p className="text-xs text-[#475569] mt-1">{new Date(o.created_at).toLocaleDateString()} · Q{o.total}</p>
                  </div>
                  <span className={`status-${o.status} px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-sm`}>{STATUS_LABELS[o.status] || o.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
